import React, { useState, useEffect, useContext } from 'react';
import { Alert } from 'antd';
import { SortOrder } from 'antd/lib/table';
import Spin from 'src/components/common/Spin';
import useQueryUrlParams from 'src/hooks/useQueryUrlParams';
import { AlertResult } from '../types/AlertResults';
import { ColumnSorting } from './AlertResultsTable/AlertResultsTable.config';
import AlertResultsTable from './AlertResultsTable/AlertResultsTable';
import useAlertResultsListFetch from '../hooks/useAlertResultsListFetch';
import { AlertResultsContext } from '../AlertResultsProvider';
import { AlertResultsComparison } from './AlertResultsComparison';
import { AlertResultExport } from './AlertResultExport';
import styles from './AlertResultsList.module.less';

type AlertResultsListProps = {
  alertId?: string;
};

export type AlertResultsQueryParams = {
  sortingcolumn: keyof AlertResult;
  sortingorder: SortOrder;
};

export const PAGE_NUMBER = 1;
export const PAGE_SIZE = 10;

const AlertResultsList: React.FC<AlertResultsListProps> = ({ alertId }): React.ReactElement => {
  const { headerFilters, sidebarFilters, forcedReload } = useContext(AlertResultsContext);
  const { sortingcolumn = 'sourceProductName', sortingorder = 'ascend' }: AlertResultsQueryParams = useQueryUrlParams();

  const [page, setPage] = useState(PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [sorting, setSorting] = useState<ColumnSorting>({ field: sortingcolumn, order: sortingorder });

  const [{ data, loading, error, total }] = useAlertResultsListFetch({
    headerFilters,
    sidebarFilters,
    sorting,
    page,
    pageSize,
    forcedReload,
  });

  const renderTotalText = () => {
    return (
      <AlertResultsComparison
        records={data}
        total={total}
        insertionDate={headerFilters.insertionDate}
        alertId={headerFilters.alertId}
        sourceOwnerId={headerFilters.sourceSiteId}
        uniqueIdentifier={headerFilters.uniqueIdentifier}
        wml={headerFilters.wml}
        variance={headerFilters.variance}
        matchType={sidebarFilters.matchType}
        priceType={sidebarFilters.priceType}
        competitorSiteId={sidebarFilters.competitorSiteId}
        competitorSellerName={sidebarFilters.competitorSellerName}
        competitorFulfillerName={sidebarFilters.competitorFulfillerName}
      />
    );
  };

  const firstLoading = loading && data === null;

  useEffect(() => {
    setPage(PAGE_NUMBER);
    setPageSize(PAGE_SIZE);
  }, [headerFilters, sidebarFilters]);

  useEffect(() => {
    if (data?.length === 0 && page > PAGE_NUMBER) {
      setPage(PAGE_NUMBER);
    }
  }, [data]);

  return (
    <>
      {error && (
        <Alert message="Error" description="Something went wrong. Please try again later..." type="error" showIcon />
      )}
      {firstLoading ? (
        <div className={styles.listLoader}>
          <Spin spinning={firstLoading} />
        </div>
      ) : (
        <>
          {data && (
            <AlertResultsTable
              items={data}
              sorting={sorting}
              loading={loading}
              page={page}
              pageSize={pageSize}
              total={total}
              totalText={renderTotalText()}
              onSortingChange={setSorting}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              alertId={alertId}
            />
          )}
          <AlertResultExport
            className={styles.exportResults}
            alertId={headerFilters.alertId}
            sourceOwnerId={headerFilters.sourceSiteId}
            insertionDate={headerFilters.insertionDate}
          />
        </>
      )}
    </>
  );
};

export default AlertResultsList;
